import { useState } from "react";
import { ChevronDown, RefreshCw, SlidersHorizontal } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";

const StratSlider = ({ label, hint, value, onChange, min, max, testId }) => (
  <div>
    <div className="flex items-center justify-between">
      <p className="text-xs font-semibold text-muted-foreground">{label}</p>
      <span className="font-mono font-bold text-xs">{value === 0 ? "uit" : `${value}%`}</span>
    </div>
    <input data-testid={testId} type="range" min={min} max={max} step="5" value={value}
      onChange={(e) => onChange(parseInt(e.target.value))} className="w-full accent-[#2D6A4F] mt-1" />
    {hint && <p className="text-[10px] text-muted-foreground">{hint}</p>}
  </div>
);

export const BatteryStartCard = ({ trip, onRefresh }) => {
  const [stratOpen, setStratOpen] = useState(false);
  const [chargeTo, setChargeTo] = useState(trip.charge_to_pct || 80);
  const [buffer, setBuffer] = useState(trip.charge_buffer_pct || 15);
  const [destMin, setDestMin] = useState(trip.dest_min_soc_pct || 0);
  const [stratBusy, setStratBusy] = useState(false);
  const stratChanged = chargeTo !== (trip.charge_to_pct || 80) || buffer !== (trip.charge_buffer_pct || 15) || destMin !== (trip.dest_min_soc_pct || 0);
  const applyStrategy = async () => {
    setStratBusy(true);
    try {
      const { data } = await api.patch(`/trips/${trip.id}/charge-strategy`, { charge_to_pct: chargeTo, charge_buffer_pct: buffer, dest_min_soc_pct: destMin });
      toast.success(`Laadstrategie toegepast — laadstops herrekend voor ${data.updated_days.length} rijdag${data.updated_days.length === 1 ? "" : "en"}`);
      onRefresh?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setStratBusy(false);
    }
  };
  return (
    <div data-testid="battery-start-card" className="bg-card rounded-2xl p-4 mb-4">
      <button data-testid="strategy-toggle" type="button" onClick={() => setStratOpen(!stratOpen)}
        className="text-sm font-semibold text-foreground hover:text-primary flex items-center gap-2 w-full transition-[color] duration-200">
        <SlidersHorizontal className="w-4 h-4 text-primary" /> Laadstrategie
        <span className="font-mono text-[11px] text-muted-foreground">laad tot {trip.charge_to_pct || 80}% · buffer {trip.charge_buffer_pct || 15}%{(trip.dest_min_soc_pct || 0) > 0 ? ` · aankomst ≥ ${trip.dest_min_soc_pct}%` : ""}</span>
        <ChevronDown className={`w-4 h-4 ml-auto transition-transform duration-200 ${stratOpen ? "rotate-180" : ""}`} />
      </button>
      {stratOpen && (
        <div data-testid="strategy-panel" className="mt-3 space-y-3 border-t border-border pt-3">
          <StratSlider testId="strategy-charge-to-slider" label="Laad bij elke stop tot" value={chargeTo} onChange={setChargeTo} min={50} max={100}
            hint="Boven 80% laadt een EV traag — 80% is meestal het snelst onderweg" />
          <StratSlider testId="strategy-buffer-slider" label="Minimale buffer bij aankomst laadstop" value={buffer} onChange={setBuffer} min={5} max={30}
            hint="Hoe lager, hoe minder stops — maar ook minder marge" />
          <StratSlider testId="strategy-arrive-slider" label="Minimaal % bij aankomst dagbestemming" value={destMin} onChange={setDestMin} min={0} max={50}
            hint="Handig als je overnachting geen lader heeft — uit = zelfde als buffer" />
          <Button data-testid="strategy-apply" size="sm" onClick={applyStrategy} disabled={stratBusy || !stratChanged}
            className="rounded-full bg-primary hover:bg-[#1B4332] w-full active:scale-95 transition-[transform,background-color] duration-200">
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${stratBusy ? "animate-spin" : ""}`} />
            {stratBusy ? "Laadstops herrekenen..." : "Pas strategie toe op alle dagen"}
          </Button>
        </div>
      )}
    </div>
  );
};
